import React, { useCallback } from 'react';

import getEnabledItems from '@lyearn/core/utils/array/getEnabledItems';
import { TFunction, Trans } from '@lyearn/i18n';
import { Editor } from '@/components/Editor';
import { ToolbarOption } from '@/components/Editor/PlainEditor/types';
import { FieldType, FormProps, useFormFieldValue } from '@/components/Form';
import { QuizCreatorQuestionError } from '@/modules/corporate/modules/quiz/views/QuizEditor/hooks/useQuizEditor/types';
import { QuestionEditorAccessType } from '@/modules/question/components/QuestionEditorModal/types';
import {
  CreateMediaInput,
  DifficultyEnum,
  DistractorInput,
  QuestionApplicableOnEnum,
  QuestionSubTypeEnum,
  QuestionTypeEnum,
  UpdateQuestionEntity,
} from '@/types/schema';

import {
  FORM_PICKLIST_PROPS,
  getQuestionTypePicklistOptions,
} from '../../../../../../../../../components/picklist';
import AddQuestionMedia from '../components/AddQuestionMedia';
import DifficultyLevel from '../components/DifficultyLevel';
import EditingInfo from '../components/EditingInfo';
import FtbIncorrectOptionsEditor from '../components/FtbIncorrectOptionsEditor';
import { LimitedEditingInfoProps } from '../components/LimitiedEditingInfo';
import QuestionSubworkspaces from '../components/QuestionSubworkspaces';
import SkillMeasured from '../components/SkillMeasured';

export const EDITOR_OPTIONS = [
  ToolbarOption.Bold,
  ToolbarOption.Italic,
  ToolbarOption.Underline,
  ToolbarOption.Strikethrough,
];

const QUESTION_TYPE_SETTINGS_LAYOUT = {
  [QuestionTypeEnum.TrueOrFalse]: ['questionType'],
  [QuestionTypeEnum.PictureMultipleChoiceQuestion]: [
    'questionType',
    'multiSelect',
    'settings.shuffleOptions',
  ],
  [QuestionTypeEnum.SingleSelect]: ['questionType', 'weightedOption', 'settings.shuffleOptions'],
  [QuestionTypeEnum.MultiSelect]: ['questionType', 'settings.shuffleOptions'],
  [QuestionTypeEnum.MatchTheFollowing]: ['questionType'],
  [QuestionTypeEnum.FillInTheBlanks]: ['questionType'],
  [QuestionTypeEnum.ImageHotspot]: ['questionType'],
} as Record<QuestionTypeEnum, string[] | undefined>;

const EMPTY_ARRAY: never[] = [];

interface Params {
  t: TFunction;
  limitedEditingInfoState?: LimitedEditingInfoProps['state'];
  allowPartialUpdates: boolean;
  question: UpdateQuestionEntity;
  initialQuestion?: Pick<UpdateQuestionEntity, 'options'>;
  onChange: (question: UpdateQuestionEntity) => void;
  error?: QuizCreatorQuestionError;
  quizSkillIds?: string[];
  accessType?: QuestionEditorAccessType;
  isQuestionSubWorkspacesEnabled: boolean;
}

export const getQuizQuestionSettingsInitialValues = (question: UpdateQuestionEntity) => {
  return {
    questionType: question.questionType,
    subType: question.subType,
    settings: question.settings,
    ...(question.questionType !== QuestionTypeEnum.ImageHotspot
      ? { attachments: question.attachments }
      : {}),
    correctAnswerFeedback: question.correctAnswerFeedback ?? '',
    incorrectAnswerFeedback: question.incorrectAnswerFeedback ?? '',
    difficulty: question.difficulty || DifficultyEnum.Na,
    skillIds: question.skillIds,
    multiSelect: question.subType === QuestionSubTypeEnum.MultiSelect,
    weightedOption: question.subType === QuestionSubTypeEnum.WeightedOption,
    globalDistractors: question.globalDistractors,
    customRatingComponent: question.customRatingComponent,
    subWorkspaceIds: question.subWorkspaceIds ? { set: question.subWorkspaceIds?.set } : undefined,
  };
};

export const getQuizQuestionSettingsFormConfig = (
  params: Params,
): Pick<FormProps, 'fields' | 'sectionLayout'> => {
  const {
    limitedEditingInfoState,
    question,
    allowPartialUpdates,
    error,
    quizSkillIds,
    t,
    accessType,
    isQuestionSubWorkspacesEnabled,
    initialQuestion,
    onChange,
  } = params;

  const fields: FormProps['fields'] = {
    limitedEditingInfo: {
      name: 'limitedEditingInfo',
      type: FieldType.SlowCustom,
      Component: () => {
        const questionType = useFormFieldValue('questionType');

        return (
          <EditingInfo
            initialQuestion={initialQuestion}
            limitedEditingInfoState={limitedEditingInfoState}
            question={question}
            questionType={questionType}
            onChange={onChange}
          />
        );
      },
    },
    questionType: {
      name: 'questionType',
      type: FieldType.Picklist,
      disabled: allowPartialUpdates,
      label: t('QuizCreator:QuestionsSettings.QuestionType'),
      picklistValues: getQuestionTypePicklistOptions(t, QuestionApplicableOnEnum.Quiz),
      props: FORM_PICKLIST_PROPS,
    },
    multiSelect: {
      name: 'multiSelect',
      type: FieldType.Switch,
      label: t('QuizCreator:QuestionsSettings.MultipleCorrectAnswers'),
      disabled: allowPartialUpdates,
    },
    weightedOption: {
      name: 'weightedOption',
      type: FieldType.Switch,
      label: t('QuizCreator:QuestionsSettings.WeightedOption'),
      disabled: allowPartialUpdates,
    },
    'settings.shuffleOptions': {
      name: 'settings.shuffleOptions',
      type: FieldType.Switch,
      tooltipHelperText: t('QuizCreator:QuestionsSettings.ShuffleOptionsDescription'),
      label: t('QuizCreator:QuestionsSettings.ShuffleOptions'),
    },
    globalDistractors: {
      name: 'globalDistractors',
      type: FieldType.SlowCustom,
      Component: ({ value, onChange: onChangeProp }) => {
        const onChange = (nextValue?: DistractorInput[]) => {
          onChangeProp({ set: nextValue || EMPTY_ARRAY });
        };

        return (
          <FtbIncorrectOptionsEditor
            error={error}
            globalDistractors={value?.set || EMPTY_ARRAY}
            onChange={onChange}
          />
        );
      },
    },
    attachments: {
      name: 'attachments',
      type: FieldType.Custom,
      Component: ({ value: attachments, onChange: onChangeProp }) => {
        const onChange = useCallback(
          (nextValue?: CreateMediaInput) => {
            onChangeProp({ set: nextValue ? [nextValue] : EMPTY_ARRAY });
          },
          [onChangeProp],
        );

        return <AddQuestionMedia media={attachments?.set?.[0]} onChange={onChange} />;
      },
    },
    difficulty: {
      name: 'difficulty',
      type: FieldType.SlowCustom,
      Component: ({ value, onChange }) => {
        return (
          <DifficultyLevel
            allowPartialUpdates={allowPartialUpdates}
            difficulty={value}
            onChange={onChange}
          />
        );
      },
    },
    skillIds: {
      name: 'skillIds',
      type: FieldType.SlowCustom,
      Component: ({ value: skillIds, onChange: onChangeProp }) => {
        const onChange = (nextValue?: string[]) => {
          onChangeProp({ set: nextValue ?? EMPTY_ARRAY });
        };

        return (
          <SkillMeasured
            allowPartialUpdates={allowPartialUpdates}
            quizSkillIds={quizSkillIds}
            value={skillIds?.set}
            onChange={onChange}
          />
        );
      },
    },
    'subWorkspaceIds.set': {
      name: 'subWorkspaceIds.set',
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return <QuestionSubworkspaces value={value} onChange={onChange} />;
      },
    },
    correctAnswerFeedback: {
      name: 'correctAnswerFeedback',
      label: t('QuizCreator:QuestionsSettings.CorrectAnswers'),
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return (
          <Editor
            className="h-160 rounded-8 border border-field"
            editorOptions={EDITOR_OPTIONS}
            placeholder={t('QuizCreator:QuestionsSettings.CorrectAnswersPlaceholder')}
            value={value ?? ''}
            variant="body-short-01"
            onChangeText={onChange}
          />
        );
      },
    },
    incorrectAnswerFeedback: {
      name: 'incorrectAnswerFeedback',
      label: t('QuizCreator:QuestionsSettings.IncorrectAnswers'),
      type: FieldType.Custom,
      Component: ({ value, onChange }) => {
        return (
          <Editor
            className="h-160 rounded-8 border border-field"
            editorOptions={EDITOR_OPTIONS}
            placeholder={t('QuizCreator:QuestionsSettings.IncorrectAnswersPlaceholder')}
            value={value ?? ''}
            variant="body-short-01"
            onChangeText={onChange}
          />
        );
      },
    },
  };

  const typeLayout = QUESTION_TYPE_SETTINGS_LAYOUT[question.questionType!] ?? [];
  const sectionLayout = getEnabledItems([
    {
      id: 'question-type-settings',
      layout: allowPartialUpdates ? ['limitedEditingInfo', ...typeLayout] : typeLayout,
      enabled: true,
    },
    {
      id: 'attachments',
      layout: ['attachments'],
      name: t('QuizCreator:QuestionsSettings.Media'),
      enabled: Boolean(
        question.questionType &&
          ![QuestionTypeEnum.FillInTheBlanks, QuestionTypeEnum.ImageHotspot].includes(
            question.questionType,
          ),
      ),
    },
    {
      id: 'incorrectOptions',
      layout: ['globalDistractors'],
      name: t('QuizCreator:QuestionsSettings.Distractors'),
      tooltipHelperText: (
        <Trans
          i18nKey={
            question.questionType === QuestionTypeEnum.FillInTheBlanks
              ? 'QuizCreator:QuestionsSettings.DistractorsFtbTooltip'
              : 'QuizCreator:QuestionsSettings.DistractorsMtfTooltip'
          }
        />
      ),
      enabled: [
        QuestionTypeEnum.FillInTheBlanks,
        QuestionTypeEnum.MatchTheFollowing,
        QuestionTypeEnum.ImageHotspot,
      ].includes(question.questionType!),
    },
    {
      id: 'details',
      layout: ['difficulty', 'skillIds'],
      name: t('QuizCreator:QuestionsSettings.Details'),
      enabled: true,
    },
    {
      id: 'answer-feedback',
      layout: ['correctAnswerFeedback', 'incorrectAnswerFeedback'],
      name: t('QuizCreator:QuestionsSettings.AnswerFeedback'),
      enabled: true,
    },
    {
      id: 'sub-workspaces',
      layout: ['subWorkspaceIds.set'],
      name: t('Common:Form.Workspaces'),
      enabled: isQuestionSubWorkspacesEnabled && accessType === 'question-bank',
    },
  ]);

  return { fields, sectionLayout };
};
